import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { clientsAPI } from '../lib/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Button } from '../components/ui/button';
import {
  Robot, ArrowUp, ArrowDown, Eye, Warning, Lightning, CircleNotch,
  CheckCircle, Shield, Prohibit
} from '@phosphor-icons/react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';
import axios from 'axios';
import { FabricatedDataNotice } from '../components/brand/FabricatedDataNotice';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('nx_token')}` } });
const KpiDot = ({ status }) => <span className="w-2 h-2 rounded-full inline-block" style={{ background: { red: 'var(--error)', amber: 'var(--warning)', green: 'var(--success)' }[status] || 'var(--text-tertiary)' }} />;
const MockB = () => <span className="px-1.5 py-0.5 text-[9px] font-mono bg-[var(--warning-bg)] text-[var(--warning)] rounded ml-1">Mock</span>;
const STS = { healthy: 'var(--success)', monitor: 'var(--warning)', alert: 'var(--error)' };

export default function AITrafficPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [sel, setSel] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggering, setTriggering] = useState('');

  useEffect(() => { clientsAPI.list().then(r => { setClients(r.data); if (r.data.length > 0) setSel(r.data[0].id); }).finally(() => setLoading(false)); }, []);
  useEffect(() => { if (!sel) return; axios.get(`${API}/ai-traffic/${sel}`, auth()).then(r => setData(r.data)).catch(() => {}); }, [sel]);

  const trigger = (agent) => { setTriggering(agent); toast.success(`${agent} agent triggered`); setTimeout(() => setTriggering(''), 1200); };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-[var(--accent-border)] border-t-[var(--accent)] rounded-full animate-spin" /></div></DashboardLayout>;
  if (!data && !loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><p className="text-[var(--text-secondary)] text-sm">Connect your first client to get started</p></div></DashboardLayout>;

  const kpi = data.kpi_cards || {};
  const lm = data.live_metrics || {};

  const vernChart = (lm.vernacular?.monthly_trend || []).map(m => ({ month: m.month, English: m.english, Hindi: m.hindi, Arabic: m.arabic, Other: m.other }));

  return (
    <DashboardLayout>
      <div className="space-y-5" data-testid="ai-traffic-page">
        <FabricatedDataNotice reason="AI Traffic has no real server-log or referral data source connected in this environment. Every value below is generated, not measured." />
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="font-display text-2xl sm:text-3xl font-black text-[var(--text-primary)] tracking-tight">AI Traffic Attribution</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-1">Raw Server Log Analysis — captures 40% of traffic GA4 misses</p>
          </div>
          <Select value={sel} onValueChange={setSel}>
            <SelectTrigger className="w-48 bg-[var(--surface-sunken)] border-[var(--border)] text-[var(--text-primary)]"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[var(--surface)] border-[var(--border)]">
              {clients.map(c => <SelectItem key={c.id} value={c.id} className="text-[var(--text-primary)] hover:bg-[var(--surface-sunken)]">{c.display_name || c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* ═══ 6 KPI CARDS ═══ */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="ai-kpis">
          <div className="nx-metric-card rounded-lg">
            <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest block mb-1">AI Visits</span>
            <p className="text-2xl font-black text-[var(--accent)] font-mono">{kpi.ai_visits?.value?.toLocaleString()}</p>
            <p className="text-[8px] text-[var(--text-tertiary)] mt-1">{kpi.ai_visits?.benchmark}</p>
          </div>
          <div className="nx-metric-card rounded-lg">
            <div className="flex items-center gap-1 mb-1"><KpiDot status={kpi.aigvr?.status} /><span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest">AIGVR</span></div>
            <p className="text-2xl font-black font-mono" style={{ color: kpi.aigvr?.status === 'green' ? 'var(--success)' : kpi.aigvr?.status === 'amber' ? 'var(--warning)' : 'var(--error)' }}>{kpi.aigvr?.value}%</p>
          </div>
          <div className="nx-metric-card rounded-lg">
            <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest block mb-1">Citation Rate</span>
            <p className="text-2xl font-black text-[var(--text-primary)] font-mono">{kpi.citation_rate?.value}%</p>
            <p className="text-[7px] text-[var(--text-tertiary)] mt-1">{kpi.citation_rate?.note}</p>
          </div>
          <div className="nx-metric-card rounded-lg">
            <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest block mb-1">Extraction Rate</span>
            <p className="text-2xl font-black text-[var(--success)] font-mono">{kpi.extraction_rate?.value}%</p>
          </div>
          <div className="nx-metric-card rounded-lg">
            <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest block mb-1">AI → Conv Rate</span>
            <p className="text-2xl font-black text-[var(--success)] font-mono">{kpi.ai_conversion?.value}%</p>
            <p className="text-[8px] text-[var(--success)] mt-1">{kpi.ai_conversion?.benchmark_label}</p>
          </div>
          <div className="nx-metric-card rounded-lg">
            <span className="text-[9px] text-[var(--text-tertiary)] font-mono uppercase tracking-widest block mb-1">Branded Lift</span>
            <p className="text-2xl font-black text-[var(--accent)] font-mono">+{kpi.branded_search_lift?.value}%</p>
            <p className="text-[7px] text-[var(--text-tertiary)] mt-1">{kpi.branded_search_lift?.note}</p>
          </div>
        </div>

        {/* ═══ CRAWLER TABLE ═══ */}
        <div className="nx-metric-card rounded-lg" data-testid="crawler-table">
          <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">
            <Robot size={16} className="inline mr-2 text-[var(--accent)]" weight="duotone" />Crawler Details (30D Server Logs)
          </h3>
          <table className="w-full text-[10px] font-mono border-collapse">
            <thead><tr className="border-b border-[var(--border)] text-[var(--text-tertiary)]">
              <th className="text-left p-2">Bot</th><th className="text-right p-2">Pages Crawled</th><th className="text-right p-2">Referrals</th><th className="text-right p-2">Ratio %</th><th className="text-right p-2">30D Trend</th><th className="text-left p-2">Status</th>
            </tr></thead>
            <tbody>
              {data.crawlers?.map(c => (
                <tr key={c.bot} className={`border-b border-[var(--border-light)] ${c.status === 'alert' ? 'bg-[var(--error-bg)]' : c.citation_risk ? 'bg-[var(--warning-bg)]' : ''}`}>
                  <td className="p-2 text-[var(--text-primary)] font-medium">{c.bot}</td>
                  <td className="p-2 text-right text-[var(--text-primary)]">{c.pages_crawled.toLocaleString()}</td>
                  <td className={`p-2 text-right ${c.referrals_sent < 5 ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>{c.referrals_sent}</td>
                  <td className="p-2 text-right text-[var(--text-primary)]">{c.ratio_pct}%</td>
                  <td className={`p-2 text-right ${c.trend_30d >= 0 ? 'text-[var(--success)]' : 'text-[var(--error)]'}`}>{c.trend_30d > 0 ? '+' : ''}{c.trend_30d}%</td>
                  <td className="p-2"><span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: STS[c.status] }} />{c.status}</span>
                    {c.citation_risk && <p className="text-[8px] text-[var(--warning)] mt-0.5">{c.citation_risk_note}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* ═══ INTELLIGENCE TABLE ═══ */}
        {data.intelligence?.length > 0 && (
          <div className="nx-metric-card rounded-lg" data-testid="ai-intelligence">
            <h3 className="font-display text-sm font-bold text-[var(--text-primary)] mb-3">
              <Lightning size={16} className="inline mr-2 text-[var(--warning)]" weight="fill" />Proactive Intelligence
            </h3>
            <table className="w-full border-collapse">
              <thead><tr className="border-b border-[var(--border)]"><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[25%]">Signal</th><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[40%]">Intelligence</th><th className="text-left text-[9px] text-[var(--text-tertiary)] font-mono uppercase p-2 w-[35%]">Action</th></tr></thead>
              <tbody>
                {data.intelligence.map(r => (
                  <tr key={r.id} className="border-b border-[var(--border-light)]">
                    <td className="p-2"><span className="flex items-center gap-2"><span className="w-2 h-2 rounded-full" style={{ background: r.severity === 'high' ? 'var(--error)' : 'var(--warning)' }} /><span className="text-xs text-[var(--text-primary)]">{r.indicator}</span></span></td>
                    <td className="p-2 text-xs text-[var(--text-secondary)]">{r.intelligence}</td>
                    <td className="p-2"><p className="text-[10px] text-[var(--text-secondary)] mb-1">{r.action}</p>
                      <Button size="sm" onClick={() => trigger(r.agent)} disabled={triggering === r.agent} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white text-[10px] h-7 px-3">
                        {triggering === r.agent ? <CircleNotch size={10} className="animate-spin mr-1" /> : <Lightning size={10} className="mr-1" />}Trigger
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ═══ 4 LIVE METRICS ═══ */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="ai-live-metrics">
          {/* M1: Machine Tax */}
          <div className="nx-metric-card rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-[var(--text-primary)]">Machine Tax (Crawl-to-Referral)</span>
              <span className="text-sm font-bold text-[var(--error)] font-mono">${lm.machine_tax?.total_machine_tax}</span>
            </div>
            <p className="text-[9px] text-[var(--text-tertiary)] mb-2">{lm.machine_tax?.total_pages_served_to_bots?.toLocaleString()} pages served to bots</p>
            {lm.machine_tax?.zero_return_bots?.length > 0 && (
              <div className="p-2 bg-[var(--error-bg)] rounded mb-2">
                <p className="text-[10px] text-[var(--error)] font-mono"><Prohibit size={10} className="inline mr-1" weight="fill" />Zero-return bots: {lm.machine_tax.zero_return_bots.join(', ')}</p>
                <p className="text-[8px] text-[var(--text-secondary)] mt-0.5">Robots.txt Business Decision recommended</p>
              </div>
            )}
            <div className="space-y-1">
              {lm.machine_tax?.bots?.slice(0, 4).map(b => (
                <div key={b.bot} className="flex items-center gap-2 text-[9px] font-mono">
                  <span className="w-20 text-[var(--text-secondary)]">{b.bot.replace('Bot', '')}</span>
                  <span className="text-[var(--text-primary)]">{b.pages_crawled.toLocaleString()}</span>
                  <span className="text-[var(--text-tertiary)]">→</span>
                  <span className={b.referrals_sent < 5 ? 'text-[var(--error)]' : 'text-[var(--success)]'}>{b.referrals_sent} refs</span>
                  <span className="text-[var(--text-tertiary)] ml-auto">${b.cost_impact}</span>
                </div>
              ))}
            </div>
          </div>

          {/* M2: AI Sentiment */}
          <div className="nx-metric-card rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-[var(--text-primary)]">AI Sentiment Polarity</span>
              {lm.sentiment?.drift_confirmed && <span className="text-[9px] bg-[var(--error-bg)] text-[var(--error)] px-2 py-0.5 rounded font-mono">Identity Drift Confirmed</span>}
            </div>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {Object.entries(lm.sentiment?.per_llm || {}).map(([l, d]) => (
                <div key={l} className="text-center p-2 rounded border border-[var(--border-light)]">
                  <p className="text-[9px] text-[var(--text-tertiary)]">{l}</p>
                  <p className="text-lg font-bold font-mono" style={{ color: d.status === 'green' ? 'var(--success)' : d.status === 'amber' ? 'var(--warning)' : 'var(--error)' }}>{d.score}</p>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-1">
              {lm.sentiment?.positive_phrases?.slice(0, 4).map(p => <span key={p} className="px-1.5 py-0.5 text-[8px] bg-[var(--success-bg)] text-[var(--success)] rounded font-mono">{p}</span>)}
              {lm.sentiment?.negative_phrases?.slice(0, 3).map(p => <span key={p} className="px-1.5 py-0.5 text-[8px] bg-[var(--error-bg)] text-[var(--error)] rounded font-mono">{p}</span>)}
            </div>
          </div>

          {/* M3: Identity Drift */}
          <div className="nx-metric-card rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-bold text-[var(--text-primary)]">Identity Drift Monitoring</span>
              <span className={`text-[9px] font-mono ${lm.identity_drift?.total_alerts > 0 ? 'text-[var(--error)]' : 'text-[var(--success)]'}`}>{lm.identity_drift?.total_alerts || 0} alerts</span>
            </div>
            {(lm.identity_drift?.alerts || []).length === 0 ? (
              <p className="text-xs text-[var(--success)] flex items-center gap-1"><CheckCircle size={14} weight="fill" /> No identity drift detected</p>
            ) : (
              <div className="space-y-2">
                {lm.identity_drift.alerts.map((a, i) => (
                  <div key={i} className="p-2 rounded border border-[var(--error-border)] bg-[var(--error-bg)]">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[10px] text-[var(--error)] font-bold">{a.type}</span>
                      <span className="text-[8px] text-[var(--text-tertiary)] font-mono">Fix: {a.fix_time}</span>
                    </div>
                    <p className="text-[9px] text-[var(--error)]">AI says: "{a.ai_says}"</p>
                    <p className="text-[9px] text-[var(--success)]">Correct: "{a.correct}"</p>
                    <p className="text-[8px] text-[var(--text-tertiary)] mt-0.5">Update: {a.pages_to_update?.join(', ')}</p>
                  </div>
                ))}
                <Button size="sm" onClick={() => trigger('identity')} className="w-full bg-[var(--error)] hover:bg-[var(--error-bg)] text-white text-[10px] h-7">
                  <Shield size={10} className="mr-1" /> Trigger Correction Campaign
                </Button>
              </div>
            )}
          </div>

          {/* M4: Vernacular Attribution */}
          <div className="nx-metric-card rounded-lg">
            <span className="text-xs font-bold text-[var(--text-primary)] mb-3 block">Vernacular Attribution</span>
            <div className="h-40 mb-3">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={vernChart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="month" tick={{ fill: 'var(--text-tertiary)', fontSize: 9 }} axisLine={false} />
                  <YAxis tick={{ fill: 'var(--text-tertiary)', fontSize: 9 }} axisLine={false} />
                  <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', fontSize: 10 }} />
                  <Bar dataKey="English" stackId="a" fill="var(--accent)" />
                  <Bar dataKey="Hindi" stackId="a" fill="var(--success)" />
                  <Bar dataKey="Arabic" stackId="a" fill="var(--warning)" />
                  <Bar dataKey="Other" stackId="a" fill="var(--error)" />
                  <Legend wrapperStyle={{ fontSize: 9 }} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <p className="text-[8px] text-[var(--text-tertiary)] font-mono uppercase mb-1">India</p>
                {Object.entries(lm.vernacular?.india || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1 text-[9px] font-mono"><span className="text-[var(--text-secondary)] w-14 capitalize">{k}</span><span className="text-[var(--text-primary)]">{v}%</span></div>
                ))}
              </div>
              <div>
                <p className="text-[8px] text-[var(--text-tertiary)] font-mono uppercase mb-1">GCC</p>
                {Object.entries(lm.vernacular?.gcc || {}).map(([k, v]) => (
                  <div key={k} className="flex items-center gap-1 text-[9px] font-mono"><span className="text-[var(--text-secondary)] w-14 capitalize">{k.replace('_', ' ')}</span><span className="text-[var(--text-primary)]">{v}%</span></div>
                ))}
              </div>
            </div>
            <p className="text-[8px] text-[var(--success)] font-mono mt-2 italic">{lm.vernacular?.benchmark}</p>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
